import { useState } from "react";
import { Sparkles, Loader2 } from "lucide-react";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { useToast } from "@/hooks/use-toast";
import { generateAIGame } from "@/services/aiGameService";
import type { Game } from "@/types/games";

interface GameGeneratorDialogProps {
  grade: string;
  /** Called once a game is generated and ready to launch. */
  onGameReady: (game: Game) => void;
  trigger?: React.ReactNode;
}

const FALLBACK_MESSAGES: Record<string, string> = {
  ai_timeout: "The AI took too long — we picked a curated set for you.",
  invalid_questions: "AI questions didn't validate — using a curated set instead.",
  api_error: "AI service hiccup — using a curated set.",
  rate_limit: "AI is busy — using a curated set for now.",
};

export const GameGeneratorDialog = ({
  grade,
  onGameReady,
  trigger,
}: GameGeneratorDialogProps) => {
  const { toast } = useToast();
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState(false);

  const [subject, setSubject] = useState("math");
  const [topic, setTopic] = useState("");
  const [difficulty, setDifficulty] = useState<"easy" | "medium" | "hard">("medium");
  const [customPrompt, setCustomPrompt] = useState("");

  const handleSubmit = async () => {
    setBusy(true);
    try {
      const result = await generateAIGame({
        grade,
        subject,
        topic: topic.trim() || undefined,
        difficulty,
        custom_prompt: customPrompt.trim() || undefined,
      });
      const title = result.fallback
        ? "Game ready (curated set)"
        : result.from_cache ? "Game ready (from cache)" : "Game generated!";
      const description = result.fallback
        ? (FALLBACK_MESSAGES[result.fallback_reason ?? ""] ?? "Using a curated question set.")
        : `"${result.game.name}" — ${result.game.estimated_time ?? "?"} min`;
      toast({ title, description });
      setOpen(false);
      onGameReady(result.game);
    } catch (e: any) {
      toast({
        title: "Couldn't generate game",
        description: e?.message ?? "Please try again with a different prompt.",
        variant: "destructive",
      });
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(v) => !busy && setOpen(v)}>
      <DialogTrigger asChild>
        {trigger ?? (
          <Button size="lg" className="gap-2">
            <Sparkles className="h-5 w-5" />
            Generate AI Game
          </Button>
        )}
      </DialogTrigger>
      <DialogContent className="max-w-lg">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Sparkles className="h-5 w-5 text-primary" />
            Create a custom game
          </DialogTitle>
          <DialogDescription>
            Describe what your learner needs to practice. The AI builds a
            grade-appropriate, kid-safe activity in seconds.
          </DialogDescription>
        </DialogHeader>

        <div className="grid gap-4 py-2">
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label htmlFor="subject">Subject</Label>
              <Select value={subject} onValueChange={setSubject}>
                <SelectTrigger id="subject"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="math">Math</SelectItem>
                  <SelectItem value="reading">Reading</SelectItem>
                  <SelectItem value="science">Science</SelectItem>
                  <SelectItem value="mixed">Mixed</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label htmlFor="difficulty">Difficulty</Label>
              <Select
                value={difficulty}
                onValueChange={(v) => setDifficulty(v as any)}
              >
                <SelectTrigger id="difficulty"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="easy">Easy</SelectItem>
                  <SelectItem value="medium">Medium</SelectItem>
                  <SelectItem value="hard">Hard</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>

          <div>
            <Label htmlFor="topic">Topic (optional)</Label>
            <Input
              id="topic"
              placeholder="e.g. multiplying fractions, syllables, water cycle"
              value={topic}
              onChange={(e) => setTopic(e.target.value)}
            />
          </div>


          <div>
            <Label htmlFor="custom">Special request (optional)</Label>
            <Textarea
              id="custom"
              placeholder="e.g. Lots of word problems with sports examples; my child loves soccer."
              value={customPrompt}
              onChange={(e) => setCustomPrompt(e.target.value)}
              maxLength={500}
              rows={3}
            />
            <p className="text-xs text-muted-foreground mt-1">
              {customPrompt.length}/500
            </p>
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => setOpen(false)} disabled={busy}>
            Cancel
          </Button>
          <Button onClick={handleSubmit} disabled={busy}>
            {busy ? (
              <>
                <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                Generating…
              </>
            ) : (
              <>
                <Sparkles className="h-4 w-4 mr-2" />
                Generate
              </>
            )}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
